import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { GitBranch, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { api } from '../utils/api';

export default function RecoverAccountPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password.length > 64) {
      toast.error('Password must be at most 64 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/recover-account', { token, new_password: password });
      if (res.ok) {
        const userData = res.data;
        if (userData.session_token) {
          localStorage.setItem('session_token', userData.session_token);
        }
        setSuccess(true);
        toast.success('Account recovered!');
        setTimeout(() => {
          setUser(userData);
          navigate('/dashboard');
        }, 1500);
      } else {
        toast.error(res.data?.detail || 'Failed to recover account');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Invalid Link</CardTitle>
            <CardDescription>This recovery link is invalid or missing a token.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="flex items-center gap-2 mb-8">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
          <GitBranch className="w-5 h-5 text-primary-foreground" />
        </div>
        <span className="font-bold text-lg">AutomataLab</span>
      </div>

      <Card className="w-full max-w-md" data-testid="recover-account-card">
        {success ? (
          <>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <CardTitle>Account Recovered</CardTitle>
              <CardDescription>Your account has been reactivated. Redirecting to your dashboard...</CardDescription>
            </CardHeader>
          </>
        ) : (
          <>
            <CardHeader>
              <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to home
              </Link>
              <CardTitle>Recover Your Account</CardTitle>
              <CardDescription>Set a new password to reactivate your account.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="8+ characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    maxLength={64}
                    data-testid="recover-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="recover-confirm-password-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="recover-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Recover Account
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
